import { V1ContentVersionOutput, V1ItemDataOutput } from '@core/api/generated'

import type {
  RawChildContentVersion,
  RawItem,
  RawItemSet,
  RawRootContentVersion,
} from './author-cloze-types'

const filterStrings = (xs: Array<string | null> | undefined): string[] =>
  (xs || []).filter((s): s is string => s != null)

const toRootContentVersionArray = (
  cv: V1ContentVersionOutput | null | undefined,
): [RawRootContentVersion] | [] => {
  if (!cv) return []
  return [
    {
      content: cv.content as RawRootContentVersion['content'],
      qualityMetrics: (cv.qualityMetrics || []) as RawRootContentVersion['qualityMetrics'],
      comments: (cv.comments || []) as RawRootContentVersion['comments'],
      submodels: filterStrings(cv.submodels),
      external_metadata: cv.externalMetadata as RawRootContentVersion['external_metadata'],
    },
  ]
}

const toChildContentVersionArray = (
  cv: V1ContentVersionOutput | null | undefined,
): [RawChildContentVersion] | [] => {
  if (!cv) return []
  return [
    {
      content: cv.content as RawChildContentVersion['content'],
      qualityMetrics: (cv.qualityMetrics || []) as RawChildContentVersion['qualityMetrics'],
      comments: (cv.comments || []) as RawChildContentVersion['comments'],
      submodels: filterStrings(cv.submodels),
    },
  ]
}

const toOriginalSubmodelsArray = (
  original: V1ItemDataOutput['originalContent'],
): [{ submodels: string[] }] | [] => {
  if (!original) return []
  const submodels = original.submodels as Array<string | null> | undefined
  return [{ submodels: filterStrings(submodels) }]
}

export const toRawClozeChild = (data: V1ItemDataOutput): RawItem => {
  // RawItem intersects Item with [Raw*]|[] for currentContent/savedContent/originalContent.
  // The intersection between Item's unwrapped {content, submodels} and []|[{...}] is
  // structurally impossible in strict mode; the legacy GraphQL path got past TS only
  // because typed-document-node coerced through `any`. Runtime shape matches RawItem.
  return {
    id: data.id,
    status: data.status,
    type: data.contentType,
    contentFilter: data.contentFilter,
    aiModelId: data.aiModel?.id,
    currentContent: toChildContentVersionArray(data.currentContent),
    savedContent: toChildContentVersionArray(data.savedContent),
    originalContent: toOriginalSubmodelsArray(data.originalContent),
  } as unknown as RawItem
}

export const toRawClozeItemSet = (
  root: V1ItemDataOutput,
  children: V1ItemDataOutput[],
): RawItemSet => {
  const { aiModel } = root
  // See note on toRawClozeChild — RawItemSet's intersection is structurally
  // impossible in strict mode; cast at the boundary to preserve runtime shape.
  return {
    id: root.id,
    status: root.status,
    type: root.contentType,
    project: root.project
      ? {
          id: root.project.id,
          name: root.project.name,
          hasAccess: root.project.hasAccess,
        }
      : null,
    aiModel: {
      name: aiModel?.name ?? '',
      flavors: aiModel?.flavors ?? {},
      externalMetadataSettings: aiModel?.externalMetadataSettings ?? null,
      type: aiModel?.type ?? '',
    },
    job: {
      id: root.job?.id ?? '',
      ownerId: root.job?.ownerId ?? '',
      selectedSubmodels: filterStrings(root.job?.selectedSubmodels),
    },
    currentContent: toRootContentVersionArray(root.currentContent),
    savedContent: toRootContentVersionArray(root.savedContent),
    originalContent: toOriginalSubmodelsArray(root.originalContent),
    children: children.map(toRawClozeChild),
  } as unknown as RawItemSet
}

/**
 * Refresh-path shape mirroring the legacy fetchItemContent GraphQL response.
 * The thunk's existing inline unwrap reads currentContent[0]/savedContent[0]/
 * originalContent[0], so the array wrapping is preserved here.
 */
type ClozeContentRefresh = {
  id: string
  status: string
  type: string
  contentFilter?: RawItem['contentFilter']
  currentContent: [RawChildContentVersion] | []
  savedContent: [RawChildContentVersion] | []
  originalContent: [{ submodels: string[] }] | []
}

export const toClozeContentRefresh = (data: V1ItemDataOutput): ClozeContentRefresh =>
  ({
    id: data.id,
    status: data.status,
    type: data.contentType,
    contentFilter: data.contentFilter,
    currentContent: toChildContentVersionArray(data.currentContent),
    savedContent: toChildContentVersionArray(data.savedContent),
    originalContent: toOriginalSubmodelsArray(data.originalContent),
  }) as unknown as ClozeContentRefresh
